import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';

/**
 * `value`, settled.
 *
 * Holds the latest value until `wait` ms have passed with no further change,
 * and - while changes keep arriving - publishes at least once every `maxWait`
 * ms so a continuously edited value never stops updating altogether.
 *
 * `leading` publishes the first change of a burst immediately. Leave it off
 * when the consumer should see only the value that survived the quiet period.
 *
 * lodash's `throttle(f, w)` is `debounce(f, w, { maxWait: w })`, so passing
 * `wait === maxWait` reproduces a plain throttle of the same period.
 */
export const useDebouncedValue = <T>(
  value: T,
  {
    wait,
    maxWait,
    leading = false,
  }: { wait: number; maxWait: number; leading?: boolean },
): T => {
  const [settled, setSettled] = useState(value);

  const push = useMemo(
    () =>
      debounce((next: T) => setSettled(() => next), wait, {
        leading,
        trailing: true,
        maxWait,
      }),
    [wait, maxWait, leading],
  );

  useEffect(() => {
    push(value);
  }, [push, value]);

  useEffect(() => () => push.cancel(), [push]);

  return settled;
};
